import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "building-your-first-plugin"
    }}>{`Building your first plugin`}</h1>
    <p>{`This tutorial will walk you through the process of building your first plugin. You will learn how to create a plugin, add a command to it, and then run it in InDesign.`}</p>
    <h2 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h2>
    <p>{`Before you start, make sure you have the following software installed on your machine:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`InDesign 2023 (v18.5)`}</strong>{` or later (can be installed from the `}<em parentName="li">{`Creative Cloud Desktop`}</em>{` app)`}</li>
      <li parentName="ul"><strong parentName="li">{`UXP Developer Tool v1.9`}</strong>{` (can be `}<a parentName="li" {...{
          "href": "https://creativecloud.adobe.com/apps/download/uxp-developer-tools"
        }}>{`installed from the `}<em parentName="a">{`Creative Cloud Desktop`}</em>{` app`}</a>{`)`}</li>
      <li parentName="ul">{`A `}<strong parentName="li">{`code editor`}</strong>{` of your choice (for example, `}<a parentName="li" {...{
          "href": "https://code.visualstudio.com/"
        }}>{`Visual Studio Code`}</a>{`)`}</li>
    </ul>
    <h2 {...{
      "id": "steps"
    }}>{`Steps`}</h2>
    <h3 {...{
      "id": "step-1-create-a-plugin"
    }}>{`Step 1: Create a plugin`}</h3>
    <p>{`You can create your plugin directly in the UXP Developer Tool. To do so, open the UXP Developer Tool and click `}<strong parentName="p">{`Create Plugin`}</strong>{`.`}</p>
    <p>{`This opens a dialog where you can enter the details for your plugin:`}</p>
    <figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "66.25%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/5530d/create-plugin.webp 320w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/0c8fb/create-plugin.webp 640w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/94b1e/create-plugin.webp 1280w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/0b34d/create-plugin.webp 1920w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/e3dad/create-plugin.webp 2214w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/dd4a7/create-plugin.png 320w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/0f09e/create-plugin.png 640w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/bbbf7/create-plugin.png 1280w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/ac7a9/create-plugin.png 1920w", "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/35d52/create-plugin.png 2214w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/indesign/uxp/static/34d8a6a31d12429b5c05ae1879c42001/bbbf7/create-plugin.png",
            "alt": "Screenshot of the create plugin dialog",
            "title": "Screenshot of the create plugin dialog",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span>{`
  `}<figcaption parentName="figure">{`The create plugin dialog`}</figcaption>
    </figure>
    <p>{`In the dialog, enter the following information:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Value`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`My First Plugin`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Host Application`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Adobe InDesign`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Host Application Version`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`18.5`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Template`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`quick-starter`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Then click `}<strong parentName="p">{`Select Folder`}</strong>{` and choose a location on your machine where you want to create the plugin.`}</p>
    <p>{`The UXP Developer Tool will create a new folder with the name you specified in the location you specified. The folder will contain the following files:`}</p>
    <pre><code parentName="pre" {...{}}>{`My First Plugin
├── manifest.json
├── index.html
└── index.js
`}</code></pre>
    <h3 {...{
      "id": "step-2-run-the-plugin"
    }}>{`Step 2: Run the plugin`}</h3>
    <p>{`Now that you have created your plugin, you can run it in InDesign.`}</p>
    <p>{`First things first, you'll need to run InDesign. You can verify that UDT is connected to the app by checking the connected apps panel.`}</p>
    <p>{`Then, in the UXP Developer Tool, in the row for your plugin, click `}<strong parentName="p">{`⋯ `}{`→`}{` Load`}</strong>{`:`}</p>
    <figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "61.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/5530d/load-plugin.webp 320w", "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/0c8fb/load-plugin.webp 640w", "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/94b1e/load-plugin.webp 1280w", "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/e3f3a/load-plugin.webp 1854w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/dd4a7/load-plugin.png 320w", "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/0f09e/load-plugin.png 640w", "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/bbbf7/load-plugin.png 1280w", "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/b7708/load-plugin.png 1854w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/indesign/uxp/static/91c2dca2aaed77336735595e074f5b06/bbbf7/load-plugin.png",
            "alt": "Screenshot of the UXP Developer Tool with the load button highlighted",
            "title": "Screenshot of the UXP Developer Tool with the load button highlighted",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span>{`
  `}<figcaption parentName="figure">{`The load button`}</figcaption>
    </figure>
    <p>{`This will load your plugin into InDesign. You should see the plugin's panel appear in InDesign:`}</p>
    <figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "63.125%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/5530d/loaded-plugin.webp 320w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/0c8fb/loaded-plugin.webp 640w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/94b1e/loaded-plugin.webp 1280w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/0b34d/loaded-plugin.webp 1920w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/c0c3e/loaded-plugin.webp 2084w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/dd4a7/loaded-plugin.png 320w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/0f09e/loaded-plugin.png 640w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/bbbf7/loaded-plugin.png 1280w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/ac7a9/loaded-plugin.png 1920w", "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/ba1e1/loaded-plugin.png 2084w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/indesign/uxp/static/0e43ea770ef9a885e43ae57244243ae0/bbbf7/loaded-plugin.png",
            "alt": "Screenshot of InDesign with the plugin panel open",
            "title": "Screenshot of InDesign with the plugin panel open",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span>{`
  `}<figcaption parentName="figure">{`The plugin panel`}</figcaption>
    </figure>
    <h3 {...{
      "id": "step-3-make-a-change"
    }}>{`Step 3: Make a change`}</h3>
    <p>{`Now that you have your plugin running in InDesign, let's make this plugin your own.`}</p>
    <p>{`Open the `}<inlineCode parentName="p">{`index.html`}</inlineCode>{` file in your code editor. You should see the following code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<html>
  <head>
    <script src="./index.js"></script>
  </head>
  <body>
    <h1>Hello World!</h1>
    <button id="btnCreateText">Create Text</button>
  </body>
</html>
`}</code></pre>
    <p>{`This is the HTML code that defines the content of your plugin's panel. You can change it to whatever you want.`}</p>
    <p>{`For example, you could change the heading to say "My First Plugin":`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<h1>My First Plugin</h1>
`}</code></pre>
    <p>{`Or you could add a paragraph below the heading:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<p>This is my first plugin.</p>
`}</code></pre>
    <p>{`To see your changes, you'll need to reload the plugin in InDesign. To do so, in the UXP Developer Tool, in the row for your plugin, click `}<strong parentName="p">{`⋯ `}{`→`}{` Reload`}</strong>{`:`}</p>
    <figure>{`
  `}<span parentName="figure" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "61.5625%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/5530d/reload-plugin.webp 320w", "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/0c8fb/reload-plugin.webp 640w", "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/94b1e/reload-plugin.webp 1280w", "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/6a1cb/reload-plugin.webp 1844w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/dd4a7/reload-plugin.png 320w", "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/0f09e/reload-plugin.png 640w", "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/bbbf7/reload-plugin.png 1280w", "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/b88a2/reload-plugin.png 1844w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/indesign/uxp/static/17af7d4f33fc57cb82835c2e862ae565/bbbf7/reload-plugin.png",
            "alt": "Screenshot of the UXP Developer Tool with the reload button highlighted",
            "title": "Screenshot of the UXP Developer Tool with the reload button highlighted",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span>{`
  `}<figcaption parentName="figure">{`The reload button`}</figcaption>
    </figure>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`If you make any changes to the `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{` file, you'll need to fully unload and reload the plugin. To do so, in the UXP Developer Tool, in the row for your plugin, click `}<strong parentName="p">{`⋯ `}{`→`}{` Unload`}</strong>{` and `}<strong parentName="p">{`⋯ `}{`→`}{` Load`}</strong>{`:`}</p>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h2>
    <p>{`Congratulations! You have successfully created your first plugin. Next, we'll take a look at some of the concepts that are important for plugin development.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      